import { Grid } from "@mui/material";
import { FormikTextField } from "@shared";
import { FormikProps } from "formik";

import { FormikClientSelect } from "@/common/components/client-select/FormikClientSelect";
import { FormikCounselorSelect } from "@/common/components/counselor-select/FormikCounselorSelect";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import { ReadOnlyCaseNoteField } from "@/common/components/read-only-case-note-field/ReadOnlyCaseNoteField";
import { ReadOnlyClientField } from "@/common/components/read-only-client-field/ReadOnlyClientField";
import { useTranslation } from "@/translations";

import { AssignmentFormValues, AssignmentMaxLength, AssignmentPriorityOption } from "./AssignmentForm.types";

interface Props {
  formik: FormikProps<AssignmentFormValues>;
  priorityOptions: AssignmentPriorityOption[];
  clientName?: string | null;
  clientId?: string | null;
  clientReadOnly?: boolean;
  caseNoteId?: string | null;
  caseNoteLabel?: string | null;
  readOnly?: boolean;
}

export function AssignmentFormFields({
  formik,
  priorityOptions,
  clientName,
  clientId,
  clientReadOnly,
  caseNoteId,
  caseNoteLabel,
  readOnly,
}: Props) {
  const { t } = useTranslation();

  return (
    <Grid container rowSpacing={2} columnSpacing={2} sx={{ pt: 2 }}>
      <Grid size={12}>
        <FormikTextField
          formik={formik}
          fieldName="description"
          label={t("assignments.creationDialog.description")}
          multiline
          rows={4}
          required
          disabled={readOnly}
          slotProps={{ input: { inputProps: { maxLength: AssignmentMaxLength.description } } }}
        />
      </Grid>
      <Grid size={12}>
        {clientReadOnly ? (
          <ReadOnlyClientField clientId={clientId} clientName={clientName} />
        ) : (
          <FormikClientSelect formik={formik} idFieldName="assignedClientId" label={t("common.client")} />
        )}
      </Grid>
      {caseNoteId && (
        <Grid size={12}>
          <ReadOnlyCaseNoteField caseNoteId={caseNoteId} caseNoteLabel={caseNoteLabel} clientId={clientId} />
        </Grid>
      )}
      <Grid size={4}>
        <FormikDatePicker
          formik={formik}
          fieldName="dueDate"
          label={t("assignments.creationDialog.dueDate")}
          required
          disabled={readOnly}
        />
      </Grid>
      <Grid size={4}>
        <FormikAutocompleteSelect
          formik={formik}
          fieldName="priority"
          label={t("assignments.creationDialog.priority")}
          options={priorityOptions}
          required
          disabled={readOnly}
        />
      </Grid>
      <Grid size={4}>
        <FormikCounselorSelect
          formik={formik}
          fieldName="assignedCounselorId"
          label={t("common.counselor")}
          required
          disabled={readOnly}
        />
      </Grid>
    </Grid>
  );
}
